import { useGesture } from '@use-gesture/react';
import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import { useRef } from 'react';

import { HistogramPanningControls } from '../HistogramPanningControls.js';
import { axisContainerStyle } from './HistogramValueBottomAxisArea.sty.js';
import { ValueBottomAxis } from '../../../../core/components/axes/components/ValueBottomAxis.js';
import { ChartGrid } from '../../../../core/components/chart-grid/ChartGridRoot.js';
import { KEYBOARD_HANDLERS } from '../../../../core/components/zoom/constants.js';
import { cursorStyle } from '../../../../core/components/zoom/ZoomCursors.sty.js';
import { AREAS } from '../../../constants/histogram-grid-area-rules.js';
import { useZoomState } from '../../../hooks/store/selectors.js';
import { useHistogramInteractions } from '../../../hooks/useHistogramInteractions.js';
import { useHistogramTickAxis } from '../../../hooks/useHistogramTickAxis.js';
import { useHistogramXScale } from '../../../hooks/useHistogramXScale.js';
import { useXAxisConfig } from '../../../hooks/useXAxisConfig.js';
import { useApplyPanDelta } from '../../overlays/hooks/useApplyPanDelta.js';

export const HistogramValueBottomAxisArea = () => {
  const axisRef = useRef<HTMLDivElement>(null);

  const { label } = useXAxisConfig();
  const ticks = useHistogramTickAxis();
  const { pan } = useHistogramInteractions();
  const { zoomOutEnabled } = useZoomState();
  const { xScale } = useHistogramXScale();
  const isPanEnabled = pan?.enabled && zoomOutEnabled;
  const applyPan = useApplyPanDelta();

  useGesture(
    {
      onDrag: (e) =>
        !KEYBOARD_HANDLERS.includes(e.type) && applyPan(-e.delta[0]),
    },
    {
      target: axisRef,
      eventOptions: { passive: false },
      drag: { filterTaps: true, pointer: { buttons: [1, 4] } },
      enabled: isPanEnabled,
    },
  );
  const bottomTicks = ticks?.bottom || { ticks: [], formattedTicks: [] };

  if (isEmpty(bottomTicks.ticks) || !xScale) {
    return null;
  }

  return (
    <ChartGrid.GridArea gridArea={AREAS.bottomAxis}>
      {({ availableWidth, availableHeight }) => {
        return (
          <div
            className={clsx(cursorStyle({ isPanEnabled }), axisContainerStyle)}
            ref={axisRef}
            data-testid="histogram-bottom-axis"
          >
            {isPanEnabled && (
              <HistogramPanningControls onClickPan={(d) => applyPan(-d)} />
            )}
            <ValueBottomAxis
              ticks={bottomTicks.ticks}
              formattedTicks={bottomTicks.formattedTicks}
              containerHeight={availableHeight}
              containerWidth={availableWidth}
              scale={xScale}
              label={label}
            />
          </div>
        );
      }}
    </ChartGrid.GridArea>
  );
};

HistogramValueBottomAxisArea['displayName'] = 'HistogramValueBottomAxisArea';
